import { useState } from "react";
import { Button } from "@datapay-by-datacom/design-system";
import { useNavigate } from "react-router";
import { useShadowRunList } from "../../hooks/useShadowRunList";
import {
  DEFAULT_RUN_LIST_FILTERS,
  type RunListFilters,
  type ShadowRunListItem,
} from "../../types";
import { getMatchRateColor } from "../../utils/colors";
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercentage,
} from "../../utils/formatters";
import {
  formatShadowRunListLabel,
  getShadowRunWorkflowId,
  shadowRunProgressPath,
} from "../../utils/workflowId";
import { ControlBar } from "../ControlBar/ControlBar";
import { hasActiveFilters } from "../ControlBar/controlBarUtils";
import { LiveProgressIndicator } from "../LiveProgressIndicator/LiveProgressIndicator";
import styles from "./BatchList.module.css";

export interface BatchListProps {
  agentId: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "var(--colours-warning-500, #f59e0b)",
  running: "var(--colours-primary-500, #3b82f6)",
  completed: "var(--colours-success-500, #22c55e)",
  failed: "var(--colours-danger-500, #ef4444)",
  terminated: "var(--colours-danger-500, #ef4444)",
};

function SkeletonRows() {
  return (
    <div data-testid="batch-list-skeleton">
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.skeleton}>
          <div className={styles.skeletonLeft}>
            <div className={styles.skeletonBlock} style={{ width: 80, height: 14 }} />
            <div className={styles.skeletonBlock} style={{ width: 60, height: 12 }} />
            <div className={styles.skeletonBlock} style={{ width: 100, height: 12 }} />
          </div>
          <div className={styles.skeletonCenter}>
            <div className={styles.skeletonBlock} style={{ width: "100%", height: 6 }} />
            <div className={styles.skeletonBlock} style={{ width: 120, height: 12 }} />
          </div>
          <div className={styles.skeletonRight}>
            <div className={styles.skeletonBlock} style={{ width: 60, height: 20 }} />
            <div className={styles.skeletonBlock} style={{ width: 50, height: 14 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  const navigate = useNavigate();

  return (
    <div className={styles.emptyState} data-testid="batch-list-empty">
      <span className={styles.emptyIcon} aria-hidden="true">
        &#9671;
      </span>
      <h3 className={styles.emptyTitle}>No Shadow Runs Yet</h3>
      <p className={styles.emptyBody}>
        Trigger your first run to compare payroll calculations and validate
        migration accuracy.
      </p>
      <Button
        variant="primary"
        onPress={() => navigate("/shadow-runs/trigger")}
      >
        Trigger Run
      </Button>
    </div>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className={styles.emptyState} data-testid="batch-list-filtered-empty">
      <span className={styles.emptyIcon} aria-hidden="true">
        &#9776;
      </span>
      <h3 className={styles.emptyTitle}>No Matching Runs</h3>
      <p className={styles.emptyBody}>
        No shadow runs match your current filters. Try adjusting or clearing
        the filters to see more results.
      </p>
      <Button variant="secondary" onPress={onClear}>
        Clear Filters
      </Button>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={styles.emptyState} data-testid="batch-list-error">
      <span className={styles.emptyIcon} aria-hidden="true">
        &#9888;
      </span>
      <h3 className={styles.emptyTitle}>Unable to Load Runs</h3>
      <p className={styles.emptyBody}>
        Something went wrong while fetching shadow runs. Check your connection
        and try again.
      </p>
      <Button variant="secondary" onPress={onRetry}>
        Retry
      </Button>
    </div>
  );
}

interface RunRowProps {
  agentId: string;
  item: ShadowRunListItem;
}

function RunRow({ agentId, item }: RunRowProps) {
  const navigate = useNavigate();
  const isActive = item.status === "running" || item.status === "pending";
  const matchRateColors = getMatchRateColor(item.matchRate);
  const workflowId = getShadowRunWorkflowId(item);
  const displayId = formatShadowRunListLabel(workflowId, item.batchId);
  const progressPath = shadowRunProgressPath(agentId, workflowId);

  return (
    <li
      className={styles.row}
      role="link"
      tabIndex={0}
      onClick={() => navigate(progressPath)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(progressPath);
        }
      }}
      aria-label={`View run ${displayId}`}
      data-testid="batch-row"
    >
      <div className={styles.rowLeft}>
        <span className={styles.runId} title={workflowId}>
          {displayId}
        </span>
        <div className={styles.statusRow}>
          <span
            className={isActive ? styles.statusDotPulsing : styles.statusDot}
            style={{ backgroundColor: STATUS_COLORS[item.status] }}
            data-testid="status-dot"
          />
          <span className={styles.statusLabel}>{item.status}</span>
        </div>
        {item.completedAt && (
          <span className={styles.date}>{formatDateTime(item.completedAt)}</span>
        )}
      </div>

      <div className={styles.rowCenter}>
        {isActive ? (
          <LiveProgressIndicator
            status={item.status as "pending" | "running"}
            matchRate={item.matchRate}
          />
        ) : (
          <div className={styles.matchRateBar}>
            <div
              className={styles.matchRateBarFill}
              style={{
                width: `${Math.min(item.matchRate, 100)}%`,
                backgroundColor: matchRateColors.color,
              }}
            />
          </div>
        )}
        {item.totalPackets > 0 && (
          <span className={styles.secondaryStats}>
            {formatNumber(item.totalPackets)} packets
            {item.durationInSeconds > 0 && (
              <>
                {" \u00B7 "}
                {formatDuration(item.durationInSeconds)}
              </>
            )}
          </span>
        )}
      </div>

      <div className={styles.rowRight}>
        <span
          className={styles.matchRate}
          style={{ color: matchRateColors.color }}
        >
          {formatPercentage(item.matchRate)}
        </span>
        <span
          className={styles.levelBadge}
          style={{
            color: matchRateColors.color,
            backgroundColor: matchRateColors.backgroundColor,
          }}
        >
          {item.matchRateStatus}
        </span>
      </div>
    </li>
  );
}

export function BatchList({ agentId }: BatchListProps) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<RunListFilters>({
    ...DEFAULT_RUN_LIST_FILTERS,
  });
  const query = useShadowRunList(agentId, filters);

  if (query.isLoading) {
    return <SkeletonRows />;
  }

  if (query.isError) {
    return <ErrorState onRetry={() => query.refetch()} />;
  }

  const allItems = query.data?.pages.flatMap((page) => page.data) ?? [];
  const isFiltered = hasActiveFilters(filters);

  // No data at all with no filters = truly empty
  if (allItems.length === 0 && !isFiltered) {
    return <EmptyState />;
  }

  function handleClearFilters() {
    setFilters({ ...DEFAULT_RUN_LIST_FILTERS });
  }

  return (
    <div>
      <ControlBar
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        triggerAction={
          <Button
            variant="primary"
            onPress={() => navigate("/shadow-runs/trigger")}
          >
            Trigger Run
          </Button>
        }
      />

      {allItems.length === 0 && isFiltered ? (
        <FilteredEmptyState onClear={handleClearFilters} />
      ) : (
        <>
          <ul className={styles.list}>
            {allItems.map((item) => (
              <RunRow
                key={getShadowRunWorkflowId(item)}
                agentId={agentId}
                item={item}
              />
            ))}
          </ul>

          {query.hasNextPage && (
            <div className={styles.loadMoreContainer}>
              <button
                className={styles.loadMoreButton}
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="load-more-button"
              >
                {query.isFetchingNextPage ? "Loading\u2026" : "Load More"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
